/**
 * 发布挂牌（单头/批量）
 * 牛只信息 + 交易设置（一口价/英式竞价）+ 素材上传 + 交割设置 → 提交审核（IR-16）
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listCattle } from '@/api/cattle';
import { createListing, listMyListings, updateListing } from '@/api/listing';
import type { Cattle, CreateListingParams, Listing } from '@/types';
import { PRICE_MAX, PRICE_MIN, QUICK_BIDS } from '@/utils/constants';
import { formatWeight } from '@/utils/format';
import PageHero from '@/components/common/PageHero';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import '@/styles/pages/listing.css';

interface UploadedImage {
  id: string;
  url: string;
}

/** ISO 时间 → <input type="datetime-local"> 值（YYYY-MM-DDTHH:mm） */
function toLocalInput(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ListingNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = Number(searchParams.get('id')) || undefined;
  const isEdit = Boolean(editId);

  const [myCattle, setMyCattle] = useState<Cattle[]>([]);
  const [editListing, setEditListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // 牛只信息
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // 交易设置
  const [tradeMode, setTradeMode] = useState<number>(1);
  const [price, setPrice] = useState('');
  const [bidIncrement, setBidIncrement] = useState(200);
  const [auctionStart, setAuctionStart] = useState('');
  const [auctionEnd, setAuctionEnd] = useState('');
  // 素材
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [description, setDescription] = useState('');
  // 交割设置
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [gpsText, setGpsText] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  // 提交
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cattleData, mineListings] = await Promise.all([
          listCattle({ page_size: 100 }),
          isEdit ? listMyListings() : Promise.resolve([] as Listing[]),
        ]);
        if (cancelled) return;

        let available = cattleData.list.filter(
          (c) => (c.sale_status ?? 0) === 0 || (c.sale_status ?? 0) === 4,
        );

        if (isEdit) {
          const listing = mineListings.find((l) => l.id === editId);
          if (!listing) {
            setLoadError('挂牌不存在或您无权编辑');
            return;
          }
          setEditListing(listing);
          // 编辑牛只可能已下架（sale_status=3），确保其出现在候选列表供只读展示
          if (listing.cattle && !available.some((c) => c.id === listing.cattle!.id)) {
            available = [listing.cattle, ...available];
          }
          setSelectedIds([listing.cattle_id]);
          setTradeMode(listing.trade_mode ?? 1);
          setPrice(listing.price != null ? String(listing.price) : '');
          setBidIncrement(listing.bid_increment ?? 200);
          if (listing.auction_start) setAuctionStart(toLocalInput(listing.auction_start));
          if (listing.auction_end) setAuctionEnd(toLocalInput(listing.auction_end));
          setImages((listing.images ?? []).map((url, i) => ({ id: `edit-${i}`, url })));
          setVideoUrl(listing.video_url ?? '');
          setDescription(listing.description ?? '');
          setDeliveryLocation(listing.delivery_location ?? '');
          if (listing.delivery_gps) {
            setGpsText(`${listing.delivery_gps.lat}, ${listing.delivery_gps.lng}`);
          }
          if (listing.delivery_time) setDeliveryTime(toLocalInput(listing.delivery_time));
        }
        setMyCattle(available);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : '加载失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, isEdit]);

  const selected = useMemo(
    () => myCattle.filter((c) => selectedIds.includes(c.id)),
    [myCattle, selectedIds],
  );
  const first = selected[0];

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const readImages = async (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files).slice(0, 6 - images.length)) {
      const reader = new FileReader();
      reader.onload = () => {
        setImages((prev) => [
          ...prev,
          { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, url: String(reader.result) },
        ]);
      };
      reader.readAsDataURL(file);
    }
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setGpsText('当前浏览器不支持定位，请手动填写');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsText(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        setDeliveryLocation(deliveryLocation || '当前位置（以 GPS 为准）');
      },
      () => setGpsText('定位失败，请手动填写'),
      { timeout: 8000 },
    );
  };

  const onSubmit = async () => {
    setSubmitError('');
    if (selectedIds.length === 0) {
      setSubmitError('请至少选择 1 头牛只');
      return;
    }
    const priceNum = Number(price);
    if (priceNum < PRICE_MIN || priceNum > PRICE_MAX) {
      setSubmitError(`价格需在 ${PRICE_MIN}~${PRICE_MAX} 元/头之间（整数）`);
      return;
    }
    if (tradeMode === 2 && (!auctionStart || !auctionEnd)) {
      setSubmitError('竞价需填写起拍/结束时间');
      return;
    }
    if (images.length < 1) {
      setSubmitError('请上传至少 1 张主图照片');
      return;
    }
    setSubmitting(true);
    try {
      // 编辑挂牌后端 PUT /listings/:id 复用完整 CreateListingRequest，必须补全必填字段
      const payload: CreateListingParams = {
        cattle_ids: selectedIds,
        trade_mode: tradeMode,
        price: priceNum,
        bid_increment: tradeMode === 2 ? bidIncrement : undefined,
        auction_start: tradeMode === 2 ? auctionStart : undefined,
        auction_end: tradeMode === 2 ? auctionEnd : undefined,
        available_qty: isEdit ? (editListing?.available_qty ?? selectedIds.length) : selectedIds.length,
        images: images.map((i) => i.url),
        video_url: videoUrl || undefined,
        description,
        delivery_location: deliveryLocation,
        delivery_gps: gpsText
          ? (() => {
              const [lat, lng] = gpsText.split(',').map((s) => Number(s.trim()));
              return { lng, lat };
            })()
          : undefined,
        delivery_time: deliveryTime || undefined,
      };
      if (isEdit && editId) {
        await updateListing(editId, payload);
      } else {
        await createListing(payload);
      }
      navigate('/my/listings');
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="listing-page">
        <PageHero eyebrow="SELL CATTLE" title={isEdit ? '编辑挂牌' : '发布挂牌'} desc={isEdit ? '修改价格 / 素材 / 交割信息，重新提交审核' : '单头 / 批量上架，平台审核后快速展示于市场'} />
        <div className="container">
          <Loading rows={5} />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="listing-page">
        <PageHero eyebrow="SELL CATTLE" title={isEdit ? '编辑挂牌' : '发布挂牌'} desc={isEdit ? '修改价格 / 素材 / 交割信息，重新提交审核' : '单头 / 批量上架，平台审核后快速展示于市场'} />
        <div className="container">
          <ErrorBox message={loadError} onRetry={() => window.location.reload()} />
        </div>
      </div>
    );
  }

  return (
    <div className="listing-page">
      <PageHero eyebrow="SELL CATTLE" title={isEdit ? '编辑挂牌' : '发布挂牌'} desc={isEdit ? '修改价格 / 素材 / 交割信息，重新提交审核' : '单头 / 批量上架，平台审核后快速展示于市场'}>
        <button className="btn btn-outline-light btn-sm" onClick={() => navigate('/my/listings')}>
          我的挂牌
        </button>
      </PageHero>

      <div className="container listing-form">
        {/* ① 牛只信息 */}
        <div className="listing-section">
          <div className="listing-section__head">
            <span className="listing-section__no">1</span>
            <strong>选择牛只（可多选批量上架）</strong>
          </div>
          {myCattle.length === 0 ? (
            <div className="listing-empty">
              暂无可用牛只。请先在牛只管理（园区端）录入并确权牛只后，再来发布挂牌。
            </div>
          ) : (
            <div className="listing-cattle-grid">
              {myCattle.map((c) => (
                <label className={`listing-cattle ${selectedIds.includes(c.id) ? 'selected' : ''}`} key={c.id}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(c.id)}
                    disabled={isEdit}
                    onChange={() => toggleSelect(c.id)}
                  />
                  <div className="listing-cattle__info">
                    <strong>{c.breed} · {c.ear_tag_no}</strong>
                    <span>{formatWeight(c.weight)} · {c.age_days} 日龄 · {c.gender === 1 ? '公' : '母'}</span>
                  </div>
                </label>
              ))}
            </div>
          )}

          {first ? (
            <div className="listing-readonly">
              <div className="form-group">
                <label className="form-label">品种 <span className="hint">自动带出</span></label>
                <input className="form-control" readOnly value={first.breed ?? ''} />
              </div>
              <div className="form-group">
                <label className="form-label">性别 <span className="hint">自动带出</span></label>
                <input className="form-control" readOnly value={first.gender === 1 ? '公' : '母'} />
              </div>
              <div className="form-group">
                <label className="form-label">出生日期 <span className="hint">自动带出</span></label>
                <input className="form-control" readOnly value={first.birth_date ?? ''} />
              </div>
              <div className="form-group">
                <label className="form-label">体重（kg） <span className="hint">自动带出</span></label>
                <input className="form-control" readOnly value={first.weight ?? ''} />
              </div>
            </div>
          ) : null}
        </div>

        {/* ② 交易设置 */}
        <div className="listing-section">
          <div className="listing-section__head">
            <span className="listing-section__no">2</span>
            <strong>交易设置</strong>
          </div>
          <div className="form-group">
            <label className="form-label"><span className="req">*</span>交易方式</label>
            <div className="form-radio-row">
              {[
                [1, '一口价'],
                [2, '竞价（英式）'],
              ].map(([v, l]) => (
                <label className="form-radio" key={v}>
                  <input
                    type="radio"
                    name="trade_mode"
                    checked={tradeMode === v}
                    onChange={() => setTradeMode(v as number)}
                  />
                  {l}
                </label>
              ))}
              <span className="form-help">荷兰式 / 密封竞价（占位，暂未开放）</span>
            </div>
          </div>

          <div className="listing-row">
            <div className="form-group">
              <label className="form-label">
                <span className="req">*</span>{tradeMode === 1 ? '一口价价格（元/头）' : '竞价底价（元/头）'}
              </label>
              <input
                className="form-control"
                type="number"
                placeholder={`${PRICE_MIN}~${PRICE_MAX}`}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              <div className="form-help">整数，含税（默认含增值税普通发票）</div>
            </div>

            {tradeMode === 2 ? (
              <>
                <div className="form-group">
                  <label className="form-label"><span className="req">*</span>最小加价幅度（元）</label>
                  <div className="form-radio-row">
                    {QUICK_BIDS.map((q) => (
                      <label className="form-radio" key={q}>
                        <input
                          type="radio"
                          name="bid_increment"
                          checked={bidIncrement === q}
                          onChange={() => setBidIncrement(q)}
                        />
                        {q}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label"><span className="req">*</span>起拍时间</label>
                  <input
                    className="form-control"
                    type="datetime-local"
                    value={auctionStart}
                    onChange={(e) => setAuctionStart(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label"><span className="req">*</span>结束时间</label>
                  <input
                    className="form-control"
                    type="datetime-local"
                    value={auctionEnd}
                    onChange={(e) => setAuctionEnd(e.target.value)}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* ③ 展示素材 */}
        <div className="listing-section">
          <div className="listing-section__head">
            <span className="listing-section__no">3</span>
            <strong>展示素材</strong>
          </div>
          <div className="form-group">
            <label className="form-label"><span className="req">*</span>主图照片 <span className="hint">≥1 张，建议 3 张以上（正面/侧面/全身）</span></label>
            <div className="listing-images">
              {images.map((img) => (
                <div className="listing-image" key={img.id}>
                  <img src={img.url} alt="主图" />
                  <button
                    type="button"
                    className="listing-image__del"
                    onClick={() => setImages((prev) => prev.filter((x) => x.id !== img.id))}
                  >
                    ×
                  </button>
                </div>
              ))}
              {images.length < 6 ? (
                <button type="button" className="listing-image listing-image--add" onClick={() => fileRef.current?.click()}>
                  ＋<span>上传</span>
                </button>
              ) : null}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                readImages(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
          <div className="listing-row">
            <div className="form-group">
              <label className="form-label">视频（选填）</label>
              <input
                className="form-control"
                placeholder="视频 URL"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">文字描述 <span className="hint">≤500 字</span></label>
              <textarea
                className="form-control"
                maxLength={500}
                placeholder="体况、饲养方式等"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ④ 交割设置 */}
        <div className="listing-section">
          <div className="listing-section__head">
            <span className="listing-section__no">4</span>
            <strong>交割设置</strong>
          </div>
          <div className="listing-row">
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>交割地（GPS 定位）</label>
              <div className="input-group">
                <input
                  className="form-control"
                  placeholder="交割地名称"
                  value={deliveryLocation}
                  onChange={(e) => setDeliveryLocation(e.target.value)}
                />
                <button className="btn btn-outline" type="button" onClick={locate}>定位</button>
              </div>
              {gpsText ? <div className="form-help">GPS：{gpsText}（交割确认时校验范围依据）</div> : null}
            </div>
            <div className="form-group">
              <label className="form-label">可交割时间</label>
              <input
                className="form-control"
                type="datetime-local"
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
              />
            </div>
          </div>
        </div>

        {submitError ? <div className="form-error">{submitError}</div> : null}

        <div className="listing-submit">
          <button className="btn btn-ghost" onClick={() => navigate('/my/listings')}>取消</button>
          <button className="btn btn-primary btn-lg" disabled={submitting} onClick={onSubmit}>
            {submitting ? '提交中…' : isEdit ? '保存修改' : '提交审核'}
          </button>
          <div className="form-help">
            {isEdit
              ? '已驳回挂牌保存后重新进入待审核，并清空原驳回理由'
              : '提交后进入待审核，审核通过自动上架展示于市场（目标 1 小时内，IR-16）'}
          </div>
        </div>
      </div>
    </div>
  );
}
